import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { GradientWash } from '@/components/generals/gradient-wash'
import { PillBadge } from '@/components/generals/pill-badge'

interface PageHeroProps {
    label: string
    titleTop: string
    titleBottom: string
    description: ReactNode
    children?: ReactNode
    className?: string
    labelDot?: ReactNode
}

/** Compact light hero shared by every inner page. */
export const PageHero: FC<PageHeroProps> = ({
    label,
    titleTop,
    titleBottom,
    description,
    children,
    className,
    labelDot
}): ReactNode => {
    return (
        <section
            id='hero'
            data-header-theme='light'
            className={cn('relative isolate overflow-hidden pb-16 pt-28 sm:pt-40 lg:pb-24 lg:pt-44', className)}
        >
            <GradientWash variant='hero' />

            <div className='shell relative'>
                <div className='max-w-4xl'>
                    <PillBadge className='animate-fade-soft' dot={labelDot}>
                        {label}
                    </PillBadge>

                    <h1 className='headline mt-6 text-[clamp(2.25rem,6.6vw,5.6rem)] text-ink [overflow-wrap:break-word]'>
                        <span className='block animate-hero-first'>
                            {titleTop}
                        </span>

                        <span className='block text-ultramarine animate-hero-line'>
                            {titleBottom}
                        </span>
                    </h1>

                    <p className='mt-6 max-w-2xl text-[17px] leading-relaxed text-ink-soft animate-fade-soft sm:text-xl' style={{ animationDelay: '700ms' }}>
                        {description}
                    </p>

                    {children && (
                        <div className='mt-9 animate-fade-soft' style={{ animationDelay: '850ms' }}>
                            {children}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
